import {
  ApiEmployee,
  ApiEmployeeCustomTableRow,
  ApiEmployeeLifecycleStatus,
} from "@/types/employees/employee.types.ts";
import { ApiDateFilter } from "@/components/date-filter/date-filter.type.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { PermissionType } from "@/types/role.types.ts";

export interface EmployeeEditFieldProps {
  forceCanView?: boolean;
  forceCanEdit?: boolean;
}

export interface ApiSection {
  id: number;
  title: string;
  datetime_created_at: Date;
  entity_type: ApiCFEntityTypes;
  section_type: ApiSectionTypes;
  tab_id: number;
  is_archived: boolean;
  is_hidden: boolean;
}

export type ApiSectionPayload = {
  title: string;
  entity_type: ApiCFEntityTypes;
  tab_type: ApiCFTabType;
  is_hidden?: boolean;
};

export enum ApiCFTabType {
  sidebar = "sidebar",
  overview = "overview",
  work_info = "work_info",
}

export enum ApiCFTabItemType {
  section = "section",
  table = "table",
}

export interface ApiCFTabSortItem {
  item_id: number;
  item_type: ApiCFTabItemType;
}

export interface ApiCFTab {
  id: number;
  type: ApiCFTabType;
  items: ApiCFTabSortItem[];
}

export enum ApiCFEntityTypes {
  employee = "employee",
  applicant = "applicant",
  job = "job",
  offer = "offer",
  employee_table = "employee_table",
}

export enum ApiSectionTypes {
  // employee_basic_info = "employee_basic_info",
  employee_basic_info_manager = "employee_basic_info_manager",
  employee_basic_info_personal = "employee_basic_info_personal",
  employee_basic_info_contacts = "employee_basic_info_contacts",
  employee_basic_info_address = "employee_basic_info_address",
  employee_basic_info_hired_via_job = "employee_basic_info_hired_via_job",
  employee_overview_summary = "employee_overview_summary",
  employee_overview_direct_reports = "employee_overview_direct_reports",
  employee_overview_teams = "employee_overview_teams",
  employee_overview_department_employees = "employee_overview_department_employees",
  employee_overview_recognitions = "employee_overview_recognitions",
  employee_basic_info_recognitions = "employee_basic_info_recognitions",
  applicant_upload_files = "applicant_upload_files",
  applicant_personal_info = "applicant_personal_info",
  applicant_contacts = "applicant_contacts",
  applicant_experience = "applicant_experience",
  job_details = "job_details",
  job_hiring_process = "job_hiring_process",
  job_compensation = "job_compensation",
  job_description = "job_description",
  offer_candidate = "offer_candidate",
  offer_terms = "offer_terms",
  offer_text = "offer_text",
  custom = "custom",
}

export interface ApiField {
  id: number;
  section_id: number;
  type: ApiFieldTypes;
  field_type: ApiFieldValueTypes;
  entity_type: ApiCFEntityTypes;
  title: string;
  description: string;
  slug: string;
  hint: string;
  is_mandatory: boolean;
  is_hidden: boolean;
  is_system_mandatory: boolean;
  is_calculated: boolean;
  is_system: boolean;
  is_historical: boolean;
  is_always_visible: boolean;
  config?: ApiFieldConfig;
  reference_entity?: ApiFieldReferenceEntity | null;
  linked_field?: ApiFieldLinkedField | null;
  is_archived: boolean;
  default_value?: ApiCustomFieldValue;
  options: CustomFieldOptionPayload[];
  table_id: number;
}

export type ApiFieldShort = Omit<
  ApiField,
  "section_id" | "is_archived" | "default_value" | "table_id"
>;

export enum ApiTableTypes {
  employee_positions = "employee_positions",
  employee_employments = "employee_employments",
  employee_terminations = "employee_terminations",
  employee_compensations = "employee_compensations",
  employee_additional_compensations = "employee_additional_compensations",
  custom = "custom",
}

export interface ApiTable {
  id: number;
  tab_id: number;
  title: string;
  slug: string;
  is_historical: boolean;
  table_fields: ApiField[];
  table_type: ApiTableTypes;
}

export interface ApiTableShort {
  id: number;
  entity_type: ApiCFEntityTypes;
  table_type: ApiTableTypes;
  title: string;
  slug: string;
  is_historical: boolean;
  table_fields: ApiFieldShort[];
}

export type ApiTablePayload = {
  tab_type: ApiCFTabType;
  title: string;
  is_historical: boolean;
};

export interface ApiFieldConfig {
  is_multiple_values: boolean;
  is_visible_to_candidate?: boolean | null;
}

export interface ApiFieldPayload {
  section_id: number;
  field_type: ApiFieldValueTypes;
  title: string;
  is_mandatory: boolean;
  is_hidden: boolean;
  is_always_visible: boolean;
  description?: string | null;
  hint?: string | null;
  config: ApiFieldConfig;
  reference_entity?: ApiFieldReferenceEntity;
  options: CustomFieldOptionPayload[] | undefined;
}

export interface ApiTableFieldPayload {
  field_type: ApiFieldValueTypes;
  title: string;
  is_mandatory: boolean;
  is_hidden: boolean;
  description?: string;
  hint?: string;
  config: ApiFieldConfig;
  reference_entity?: ApiFieldReferenceEntity;
  linked_field?: ApiFieldLinkedField;
  options: CustomFieldOptionPayload[] | undefined;
}

export interface ApiSystemFieldPayload {
  title?: string | null;
  description: string | null;
  hint: string | null;
  is_mandatory?: boolean;
  is_hidden: boolean;
}

export interface ApiFieldOptionsPayload {
  options: CustomFieldOptionPayload[];
}

export interface ApiFieldDefaultValuePayload {
  default_value?: ApiCustomFieldValue;
  upsert_to_all_entities: boolean;
}

export enum ApiFieldTypes {
  employee_position_job_title = "employee_position_job_title",
  employee_position_job_profile = "employee_position_job_profile",
  employee_position_reporting_to = "employee_position_reporting_to",
  employee_employment_legal_entity = "employee_employment_legal_entity",
  employee_employment_type = "employee_employment_type",
  employee_employment_contract = "employee_employment_contract",
  employee_employment_work_pattern = "employee_employment_work_pattern",
  employee_employment_comment = "employee_employment_comment",
  employee_compensation_base_salary_gross = "employee_compensation_base_salary_gross",
  employee_compensation_base_salary_net = "employee_compensation_base_salary_net",
  employee_compensation_currency = "employee_compensation_currency",
  employee_compensation_per_type = "employee_compensation_per_type",
  employee_compensation_change_reason = "employee_compensation_change_reason",
  employee_compensation_comment = "employee_compensation_comment",
  employee_compensation_gross_total = "employee_compensation_gross_total",
  employee_compensation_net_total = "employee_compensation_net_total",
  employee_compensation_total_cost = "employee_compensation_total_cost",
  employee_compensation_lines = "employee_compensation_lines",
  employee_additional_compensation_compensation_type = "employee_additional_compensation_compensation_type",
  employee_additional_compensation_amount_gross = "employee_additional_compensation_amount_gross",
  employee_additional_compensation_amount_net = "employee_additional_compensation_amount_net",
  employee_additional_compensation_currency = "employee_additional_compensation_currency",
  employee_additional_compensation_description = "employee_additional_compensation_description",
  employee_additional_compensation_comment = "employee_additional_compensation_comment",
  employee_position_division = "employee_position_division",
  employee_position_location = "employee_position_location",
  employee_position_department = "employee_position_department",
  employee_position_change_reason = "employee_position_change_reason",
  employee_position_comment = "employee_position_comment",
  employee_number = "employee_number",
  employee_email = "employee_email",
  employee_date_birth = "employee_date_birth",
  employee_email_personal = "employee_email_personal",
  employee_phone = "employee_phone",
  employee_phone_personal = "employee_phone_personal",
  employee_slack = "employee_slack",
  employee_address_address_line_1 = "employee_address_address_line_1",
  employee_address_address_line_2 = "employee_address_address_line_2",
  employee_address_country = "employee_address_country",
  employee_address_state = "employee_address_state",
  employee_address_city = "employee_address_city",
  employee_address_zip_code = "employee_address_zip_code",
  employee_first_name = "employee_first_name",
  employee_last_name = "employee_last_name",
  employee_middle_name = "employee_middle_name",
  employee_name_pronunciation = "employee_name_pronunciation",
  employee_gender = "employee_gender",
  employee_hired_on = "employee_hired_on",
  employee_lifecycle_status = "employee_lifecycle_status",
  employee_last_activity_date = "employee_last_activity_date",
  employee_timezone = "employee_timezone",
  employee_employment_probation_date_end = "employee_employment_probation_date_end",
  employee_employment_termination_date = "employee_employment_termination_date",
  employee_termination_last_working_date = "employee_termination_last_working_date",
  employee_termination_leave_type = "employee_termination_leave_type",
  employee_termination_reason = "employee_termination_reason",
  employee_termination_replacement_employee = "employee_termination_replacement_employee",
  employee_termination_eligible_for_rehire = "employee_termination_eligible_for_rehire",
  employee_termination_comment = "employee_termination_comment",

  applicant_cv = "applicant_cv",
  applicant_first_name = "applicant_first_name",
  applicant_position = "applicant_position",
  applicant_company = "applicant_company",
  applicant_last_name = "applicant_last_name",
  applicant_photo = "applicant_photo",
  applicant_salary = "applicant_salary",
  applicant_middle_name = "applicant_middle_name",
  applicant_location = "applicant_location",
  applicant_email = "applicant_email",
  applicant_telegram = "applicant_telegram",
  applicant_phone = "applicant_phone",
  applicant_date_birth = "applicant_date_birth",
  applicant_additional_contacts = "applicant_additional_contacts",

  job_title = "job_title",
  job_public_title = "job_public_title",
  job_department = "job_department",
  job_division = "job_division",
  job_job_profile = "job_job_profile",
  job_location = "job_location",
  job_priority = "job_priority",
  job_pipeline = "job_pipeline",
  job_approval_policy = "job_approval_policy",
  job_recruiters = "job_recruiters",
  job_stakeholders = "job_stakeholders",
  job_salary_min = "job_salary_min",
  job_salary_max = "job_salary_max",
  job_currency = "job_currency",
  job_description = "job_description",
  job_requirements = "job_requirements",
  job_responsibilities = "job_responsibilities",
  job_conditions = "job_conditions",
  job_files = "job_files",
  offer_first_name = "offer_first_name",
  offer_last_name = "offer_last_name",
  offer_job_title = "offer_job_title",
  offer_date_start = "offer_date_start",
  offer_legal_entity = "offer_legal_entity",
  offer_location = "offer_location",
  offer_manager = "offer_manager",
  offer_salary = "offer_salary",
  offer_text = "offer_text",

  effective_from = "effective_from",
  effective_to = "effective_to",
  changed_by = "changed_by",

  table = "table",
  custom = "custom",
}

export enum ApiFieldValueTypes {
  text = "text",
  textarea = "textarea",
  date = "date",
  datetime = "datetime",
  number = "number",
  percentage = "percentage",
  select = "select",
  switch = "switch",
  currency = "currency",
  employee_reference = "employee_reference",
  entity_reference = "entity_reference",
  phone = "phone",
  email = "email",
  url = "url",
  file = "file",
  image = "image",
  text_array = "text_array",
  employee_compensation_lines = "employee_compensation_lines",
}

/** System fields a reference column can be linked to; one column per linked field in a table. */
export type ApiFieldLinkedField =
  | ApiFieldTypes.employee_employment_legal_entity
  | ApiFieldTypes.employee_position_department
  | ApiFieldTypes.employee_position_division
  | ApiFieldTypes.employee_position_location;

export enum ApiFieldReferenceEntity {
  legal_entity = "legal_entity",
  department = "department",
  division = "division",
  location = "location",
}

export const LINKED_FIELD_BY_REFERENCE_ENTITY: Record<
  ApiFieldReferenceEntity,
  ApiFieldLinkedField
> = {
  [ApiFieldReferenceEntity.legal_entity]: ApiFieldTypes.employee_employment_legal_entity,
  [ApiFieldReferenceEntity.department]: ApiFieldTypes.employee_position_department,
  [ApiFieldReferenceEntity.division]: ApiFieldTypes.employee_position_division,
  [ApiFieldReferenceEntity.location]: ApiFieldTypes.employee_position_location,
};

export interface CustomFieldInformation {
  title: string;
  description: string;
  type: ApiFieldValueTypes;
}

export interface CustomFieldOption {
  id: number;
  title: string;
  description: string;
  is_archived: boolean;
}

export type CustomFieldOptionPayload = Omit<CustomFieldOption, "id" | "is_archived"> & {
  id?: number | undefined;
};

export interface ApiCustomFieldRoles {
  custom_field_id: number;
  items: ApiCustomFieldRolesItem[];
}

export interface ApiCustomFieldRolesItem {
  role_id: number;
  permission_value: PermissionType;
}

export interface ApiCustomField {
  field: ApiField;
  permission_value: PermissionType;
  value: ApiCustomFieldValue | null;
}

export interface ApiCustomFieldValue {
  value_int?: number | null;
  value_float?: number | null;
  value_text?: string | null;
  value_label?: string | null;
  value_bool?: boolean | null;
  datetime_value?: Date | null;
  date_value?: Date | null;
  value_int_array?: number[] | null;
  value_text_array?: string[] | null;
  value_file?: ApiCustomFieldFile | null;
  value_employees?: ApiEmployee[] | null;
  value_references?: ApiCustomFieldReference[] | null;
}

export interface ApiCustomFieldReference {
  id: number;
  title: string;
  reference_entity: ApiFieldReferenceEntity;
  is_archived: boolean;
}

export interface ApiCustomFieldFile {
  name: string;
  size: number;
  mime_type: string;
  url: string;
}

export interface ApiCFEmployeeTableRow extends ApiEmployeeCustomTableRow {
  employee: ApiEmployee;
}

export interface ApiCFTableFieldFilter {
  slug: string;
  field_type: ApiFieldValueTypes;

  text?: string;
  number_from?: number;
  number_to?: number;
  date_filter?: ApiDateFilter;
  datetime_from?: Date;
  datetime_to?: Date;
  option_ids?: number[];
  switch?: boolean;
  employee_ids?: number[];
  reference_ids?: number[];
}

export interface ApiCFTableRowFilter extends ApiFilterBase {
  text?: string;
  employee_ids?: number[];
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  employee_lifecycle_statuses?: ApiEmployeeLifecycleStatus[];
  date_effective_from?: Date;
  date_effective_to?: Date;
  field_filters?: ApiCFTableFieldFilter[];
  sorting?: ApiSorting;
  /** Историческая таблица: true — вся история, иначе только актуальные строки */
  with_history?: boolean;
}

export enum CFTableRowSortColumn {
  employee = "employee",
  effective_from = "effective_from",
}

export interface ApiEmployeeTab {
  id: number;
  title: string;
  type: ApiCFTabType;
}

export interface ApiCFConfigField extends Pick<
  ApiField,
  | "id"
  | "slug"
  | "type"
  | "field_type"
  | "title"
  | "is_mandatory"
  | "is_system_mandatory"
  | "is_system"
  | "is_calculated"
> {
  hint: string | null;
  description?: string | null;
  is_always_visible?: boolean;
  config: ApiFieldConfig | null;
  reference_entity: ApiFieldReferenceEntity | null;
  options: (Omit<CustomFieldOption, "description"> & { description: string | null })[];
  default_value: ApiCustomFieldValue | null;
}
export interface ApiCFConfigSection extends Pick<ApiSection, "id" | "title" | "section_type"> {
  fields: ApiCFConfigField[];
}
export interface ApiCFEntityConfig {
  sections: ApiCFConfigSection[];
}
